<template>
	<div class="balance-chart">
		<AppChart
			expandible
			height="290px"
			full-width
			:caption="getString('transactions')"
			:value="chartData.totalAmount"
			:last-period-value="chartData.totalAmountPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="chartData.series"
			:xaxis="chartData.xaxis"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:key="refreshKey"
		/>
	</div>
</template>

<script>
import {useCardsStore} from "@/console-new/stores/cards.store";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import AppChart from "@/shared/components/AppChart.vue";

export default {
	components: {AppChart},
	setup() {
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {
			cardsStore,
			currencyStore,
			getString: languageStore.getString,
			userStore
		}
	},
	name: "BalanceChart",
	data() {
		return {
			transactionData: null,
			loading: false,
			refreshKey: 0
		}
	},
	computed: {
		chartData() {
			if (!this.transactionData || !this.transactionData.authorizations || !this.transactionData.transactions) {
				return {
					totalAmount: 0,
					totalAmountPrevious: 0,
					series: [],
					xaxis: []
				};
			}

			// Generate last 7 days for display
			const days = [];
			const today = new Date();
			for (let i = 6; i >= 0; i--) {
				const date = new Date(today);
				date.setDate(date.getDate() - i);
				days.push({
					date: date,
					label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
				});
			}

			// Generate previous 7 days (8-14 days ago)
			const previousDays = [];
			for (let i = 13; i >= 7; i--) {
				const date = new Date(today);
				date.setDate(date.getDate() - i);
				previousDays.push({
					date: date,
					label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
				});
			}

			// Initialize data arrays
			const currentPeriodData = new Array(7).fill(0);
			const previousPeriodData = new Array(7).fill(0);

			let totalAmount = 0;
			let totalAmountPrevious = 0;

			// Process all cards' authorizations
			this.transactionData.authorizations.forEach(auth => {
				if (['closed', 'expired', 'reversed', 'declined'].includes(auth.status)) {
					return;
				}

				const authDate = auth.date;
				
				// Check current period
				const currentDayIndex = this.getDayIndex(authDate, days);
				if (currentDayIndex !== -1) {
					const amount = this.currencyStore.dbToActualValue(auth.amount);
					currentPeriodData[currentDayIndex] += amount;
					totalAmount += amount;
				}

				// Check previous period
				const previousDayIndex = this.getDayIndex(authDate, previousDays);
				if (previousDayIndex !== -1) {
					const amount = this.currencyStore.dbToActualValue(auth.amount);
					previousPeriodData[previousDayIndex] += amount;
					totalAmountPrevious += amount;
				}
			});

			// Process all cards' transactions
			this.transactionData.transactions.forEach(txn => {
				const txnDate = txn.date;
				
				let amount = this.currencyStore.dbToActualValue(txn.amount);
				
				// Make captures negative, refunds positive
				if (txn.type === 'capture') {
					amount = -amount;
				}
				
				// Check current period
				const currentDayIndex = this.getDayIndex(txnDate, days);
				if (currentDayIndex !== -1) {
					currentPeriodData[currentDayIndex] += amount;
					totalAmount += amount;
				}

				// Check previous period
				const previousDayIndex = this.getDayIndex(txnDate, previousDays);
				if (previousDayIndex !== -1) {
					previousPeriodData[previousDayIndex] += amount;
					totalAmountPrevious += amount;
				}
			});

			return {
				totalAmount: totalAmount,
				totalAmountPrevious: totalAmountPrevious,
				series: [
					{
						name: this.getString('previous_period'),
						data: previousPeriodData
					},
					{
						name: this.getString('current_period'),
						data: currentPeriodData
					}
				],
				xaxis: days.map(day => day.label)
			};
		}
	},
	methods: {
		getDayIndex(date, days) {
			const dateStr = date.toDateString();
			return days.findIndex(day => day.date.toDateString() === dateStr);
		},
		fetchAllCardsTransactionData() {
			// Calculate start date (14 days ago) to get both current and previous periods
			const startDate = new Date();
			startDate.setDate(startDate.getDate() - 14);
			const startDateStr = startDate.toISOString().split('T')[0] + ' 00:00:00';

			this.loading = true;
			// Fetch transactions for all cards (null cardId)
			this.cardsStore.getCardTransactions(null, startDateStr, (data) => {
				this.loading = false;
				this.transactionData = data;
				// Force chart refresh
				this.refreshKey++;
			});
		}
	},
	mounted() {
		// Delay initial fetch to ensure DOM is ready
		this.$nextTick(() => {
			this.fetchAllCardsTransactionData();
		});
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== oldVal) {
				this.fetchAllCardsTransactionData();
			}
		},
		'cardsStore.showingCards': function (newVal) {
			// Only fetch if we're showing the balance view (not cards)
			if (!newVal) {
				this.$nextTick(() => {
					this.fetchAllCardsTransactionData();
				});
			}
		}
	}
}
</script>

<style scoped lang="scss">

</style>